import { Box, Button, Card, CardContent, Typography } from "@mui/material"
import { styled } from "@mui/material/styles"
import { useEffect, useMemo, useRef, useState } from "react"
import styles from "@/components/CRM/Search.module.css"
import { tableCellClasses } from "@mui/material/TableCell"
import AddIcon from "@mui/icons-material/Add"
import { toast } from "sonner"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiFetch } from "@/hooks/apiFetch"
import { useCrudContext } from "@/context/crudContext"
import { useCustomForm } from "@/hooks/useCustomForm"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"

const UpdatePriceViaImport = () => {
  const queryClient = useQueryClient()
  const apiPath = "product-price-update"
  const revalidateQuery = "products-price-updates"
  const importSchema = z.object({
    file: z.instanceof(File, { message: "Please select a file" }),
  })

  const {
    api,
    handleSubmit: handleImportSubmit,
    isSubmitting: importIsSubmitting,
    reset: importReset,
    control: importControl,
    Controller: ImportController,
  } = useCustomForm({
    resolver: zodResolver(importSchema),
  })

  const submitImport = async (data) => {
    try {
      const blob = new Blob([data.file], { type: data.file.type })
      const formData = new FormData()
      formData.append("file", blob, data.file.name)

      const method = "POST"
      const response = await api(`import/${apiPath}`, {
        method,
        body: formData,
        headers: {},
      })

      if (response) {
        handleImportReset()
        queryClient.invalidateQueries({
          queryKey: revalidateQuery ? [revalidateQuery] : [apiPath],
        })
        toast.success(`Imported successfully`)
      }
    } catch (error) {
      console.error("Import failed:", error)
      toast.error("Failed to import file")
    }
  }

  const handleImportReset = () => {
    importReset({
      file: undefined,
    })
  }

  return (
    <Card sx={{ maxWidth: 400, margin: "auto", boxShadow: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Update Product Prices
        </Typography>
        <form onSubmit={handleImportSubmit(submitImport)}>
          <Box sx={{ mb: 3 }}>
            <ImportController
              name="file"
              control={importControl}
              render={({ field: { onChange }, fieldState: { error } }) => (
                <>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error("File size should be less than 5MB")
                          return
                        }
                        onChange(file)
                      }
                    }}
                    className="form-control"
                    placeholder="Choose file"
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                    }}
                  />
                  {error && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ display: "block", mt: 1 }}
                    >
                      {error.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="outlined"
              disabled={importIsSubmitting}
              onClick={() => {
                setShowImportDialog(false)
                handleImportReset()
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={importIsSubmitting}
            >
              Submit
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  )
}

export default UpdatePriceViaImport
